/**
 * ChatComposer — extracted from ConversationsChat.js (Session 5 refactor).
 * Jul 2026
 *
 * Purely presentational bottom input strip:
 *   • Mic button (Sprint 207W: currently SUSPENDED but retained so it can
 *     be re-enabled by simply re-flipping speechSupported)
 *   • Text input
 *   • "Stop response" button (only visible while AvA is speaking / sending)
 *   • Submit button
 *   • Below the form: "listening…" pill, mic-suppressed pill, or the
 *     dismissable mic-error banner (whichever applies)
 *
 * Byte-identical to the pre-extraction JSX. Parent still owns `message`,
 * `sending`, the submit handler, and every speech-recognition callback.
 */
import React from 'react';
import { Mic, MicOff, Send, Square, VolumeX, Loader2 } from 'lucide-react';

export const ChatComposer = ({
  message,
  setMessage,
  handleSendMessage,
  sending,
  speechSupported,
  isListening,
  isSpeaking,
  startListening,
  stopListening,
  stopSpeaking,
  selectedAvaName,
  micError,
  setMicError,
}) => {
  return (
    <div className="p-2.5 sm:p-4 border-t border-gray-200 bg-white">
      <form onSubmit={handleSendMessage} className="flex items-center gap-2 sm:gap-3">
        {/* Sprint 207W — Mic input SUSPENDED for launch stability.
            Audio output (Text / Voice / Both) remains live via the
            interactionMode toggle. Underlying speech-recognition
            handlers (startListening/stopListening/recognitionRef)
            are intentionally retained so this can be re-enabled
            by simply restoring the button JSX. */}
        {speechSupported && (
          <button
            type="button"
            onClick={isListening ? stopListening : startListening}
            disabled={isSpeaking || sending}
            className={`p-2.5 sm:p-3 rounded-full transition-all flex items-center gap-2 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed ${
              isListening
                ? 'bg-red-500 text-white animate-pulse px-3 sm:px-4'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="mic-btn"
            title={
              isSpeaking
                ? 'AvA is speaking — wait for her to finish'
                : isListening ? 'Stop listening' : 'Tap to speak'
            }
          >
            {isListening ? (
              <>
                <MicOff size={18} />
                <span className="text-sm font-medium hidden sm:inline">Stop</span>
              </>
            ) : (
              <Mic size={18} />
            )}
          </button>
        )}

        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={isListening ? 'Listening...' : `Message ${selectedAvaName}...`}
          className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
          disabled={sending || isListening}
          data-testid="chat-input"
        />

        {/* Stop Response Button */}
        {(isSpeaking || sending) && (
          <button
            type="button"
            onClick={stopSpeaking}
            className="p-2.5 sm:p-3 bg-black text-white rounded-full hover:bg-gray-800 transition-colors flex-shrink-0"
            data-testid="stop-response-btn"
            title="Stop Response"
          >
            <Square size={18} fill="currentColor" />
          </button>
        )}

        <button
          type="submit"
          disabled={!message.trim() || sending}
          className="p-2.5 sm:p-3 bg-purple-500 text-white rounded-full hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          data-testid="send-message-btn"
        >
          {sending ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
        </button>
      </form>

      {isListening && (
        isSpeaking ? (
          <p
            className="text-xs text-center text-purple-700 mt-2 flex items-center justify-center gap-1.5"
            data-testid="mic-suppressed-pill"
          >
            <VolumeX size={12} className="animate-pulse" />
            {selectedAvaName} is speaking — I&apos;ll pause listening
          </p>
        ) : (
          <p className="text-xs text-center text-red-600 mt-2 animate-pulse">
            🎤 Listening… tap Stop when finished, then review and press Send
          </p>
        )
      )}

      {micError && (
        <div
          className="mt-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start gap-2"
          role="alert"
          data-testid="mic-error-banner"
        >
          <span aria-hidden>⚠️</span>
          <span className="flex-1">{micError}</span>
          <button
            onClick={() => setMicError(null)}
            className="text-amber-900 hover:text-amber-700 font-bold leading-none"
            aria-label="Dismiss mic error"
            data-testid="mic-error-dismiss"
          >×</button>
        </div>
      )}
    </div>
  );
};

export default ChatComposer;
